import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AppAppBar from '../components/AppAppBar';
import Footer from '../components/Footer';
import { Section } from '../components/Section';
import Reveal from '../components/Reveal';
import { Seo, serviceJsonLd } from '../components/Seo';
import { contactPath, routePath } from '../seo/routes';
import { azure, glassHover, line } from '../theme';

/**
 * /fawtara and /ar/fawtara: connecting a system you already run to Oman
 * e-invoicing.
 *
 * ── The two offers, said out loud ───────────────────────────────────────────
 * Dynacloud does this as a service. Nizro has the structure built in. They are
 * different answers to the same problem, and a reader deciding between them is
 * better served by a page that says so than by one pretending only one exists.
 * The last section links across, which is also why this page is allowed to
 * mention a competitor of its own product.
 *
 * ── What this page must never say ──────────────────────────────────────────
 * Dynacloud is not accredited, holds no Access Point, and this is a NEW
 * offering with no delivered Fawtara project behind it. The copy in the locale
 * files was written and reviewed under that constraint, and one review pass
 * had to remove three real client names from it: they are genuine consultancy
 * clients, and putting them on this page would imply Fawtara work that has not
 * happened.
 *
 * The body renders whatever the locale file holds, because the Arabic argument
 * is not a translation of the English one and does not have the same number of
 * moves. Nine sections in Arabic against six in English is the two languages
 * being written properly, not a file that drifted.
 */
export default function FawtaraPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const copy = t('fawtara', { returnObjects: true });
  const sections = Array.isArray(copy.sections) ? copy.sections : [];
  const faq = Array.isArray(copy.faq) ? copy.faq : [];

  return (
    <Box sx={{ bgcolor: 'background.default', color: 'text.primary' }}>
      <Seo
        page="fawtara"
        path="/fawtara"
        jsonLd={serviceJsonLd({
          lang,
          path: '/fawtara',
          name: copy.h1,
          description: copy.metaDescription,
          faq,
        })}
      />
      <AppAppBar />

      <Section id="fawtara" sx={{ pt: { xs: 16, md: 20 } }}>
        <Reveal>
          <Typography variant="overline" component="p" sx={{ color: azure[300] }}>
            {t('nav.fawtara')}
          </Typography>
          <Typography variant="h2" component="h1" sx={{ maxWidth: 900, mt: 1 }}>
            {copy.h1}
          </Typography>
          <Typography
            sx={{ maxWidth: 780, mt: 2, fontSize: { xs: 17, md: 19 }, lineHeight: 1.75, color: 'text.secondary' }}
          >
            {copy.lead}
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ mt: 4, flexWrap: 'wrap', gap: 1.5 }}>
            <Button
              component={RouterLink}
              to={contactPath(lang, 'fawtara')}
              variant="contained"
              endIcon={<ArrowForwardRounded sx={{ transform: lang === 'ar' ? 'scaleX(-1)' : 'none' }} />}
            >
              {copy.cta || t('nav.contact')}
            </Button>
            <Button component={RouterLink} to={routePath(lang, '/nizro')} variant="outlined">
              {t('nav.nizro')}
            </Button>
          </Stack>
        </Reveal>
      </Section>

      {sections.map((section, i) => (
        <Section key={section.heading} sx={{ pt: 0 }}>
          <Reveal>
            <Typography variant="overline" sx={{ color: azure[300] }}>
              {String(i + 1).padStart(2, '0')}
            </Typography>
            <Typography variant="h4" component="h2" sx={{ mt: 1, mb: 2, maxWidth: 820 }}>
              {section.heading}
            </Typography>
            <Typography sx={{ maxWidth: 820, lineHeight: 1.85, color: 'text.secondary', whiteSpace: 'pre-line' }}>
              {section.body}
            </Typography>
            {Array.isArray(section.bullets) && section.bullets.length ? (
              <Stack component="ul" sx={{ listStyle: 'none', p: 0, mt: 3, gap: 1.5, maxWidth: 820 }}>
                {section.bullets.map((b) => (
                  <Stack key={b} component="li" direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                    <CheckRounded sx={{ fontSize: 20, color: azure[300], mt: '3px', flexShrink: 0 }} />
                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.8 }}>{b}</Typography>
                  </Stack>
                ))}
              </Stack>
            ) : null}
          </Reveal>
        </Section>
      ))}

      {/* The questions, in the page's HTML whether or not anything is open:
          the FAQPage graph above quotes these exact strings, and markup that
          describes content a crawler cannot see is the thing Google penalises. */}
      {faq.length ? (
        <Section sx={{ pt: 0 }}>
          <Reveal>
            <Typography variant="h4" component="h2" sx={{ mb: 3 }}>
              {t('faq.title', { defaultValue: lang === 'ar' ? 'أسئلة متكررة' : 'Common questions' })}
            </Typography>
            <Box sx={{ maxWidth: 900, borderTop: `1px solid ${line}` }}>
              {faq.map((item) => (
                <Box
                  key={item.q}
                  component="details"
                  sx={{
                    borderBottom: `1px solid ${line}`,
                    '&[open]': { bgcolor: alpha('#fff', 0.02) },
                    '&:hover': { bgcolor: glassHover },
                  }}
                >
                  <Box
                    component="summary"
                    sx={{
                      cursor: 'pointer',
                      listStyle: 'none',
                      py: 2.5,
                      fontWeight: 600,
                      '&::-webkit-details-marker': { display: 'none' },
                    }}
                  >
                    {item.q}
                  </Box>
                  <Typography sx={{ pb: 2.5, color: 'text.secondary', lineHeight: 1.85 }}>
                    {item.a}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Reveal>
        </Section>
      ) : null}

      <Footer />
    </Box>
  );
}
